import {
    OnGetCallLink,
    OnNewCall,
    OnDispCall,
    ParamsType,
    OnSubmitLog,
    SubmitCall,
    OnEndCall,
    CallBase,
    CallLogStorage,
    CallLink,
    SourceType,
} from 'rcx_adapter';

import { BaseModule } from './BaseModule';
import { ModuleBaseOption } from '../interfaces/module';
import { mapLogInfo } from '../utils/mapData';

class CallLogModule extends BaseModule<ModuleBaseOption> {
    public start(): void {
        if (this.getIsInit()) {
            this.listenFromRCX();
        }
    }
    private listenFromRCX() {
        if (this.mRCXAdapter && this.mServiceNowAdapter) {
            this.mRCXAdapter.onEndCall(this._handleEndCall.bind(this));
            this.mRCXAdapter.onNewCall(this._handleNewCall.bind(this));
            this.mRCXAdapter.onGetCallLink(this._handleGetCallLink.bind(this));
            this.mRCXAdapter.onSubmitLog(this._handleSubmitLog.bind(this));
            this.mRCXAdapter.onPendingDisposition(this._handlePendingDisposition.bind(this));
            this.mRCXAdapter.onAcdCallbackReady(this._handleAcdCallbackReady.bind(this));
        }
    }

    private async _handleEndCall(data: OnEndCall) {
        const logAdapter = this.mServiceNowAdapter!.getLogAdapter();
        await logAdapter.updateStorageLogAndContact({
            paramsType: ParamsType.CALL,
            rawData: {
                recordingUrl: data.recordingUrl,
                transcriptUrl: data.transcriptUrl,
                duration: data.duration,
                isEnded: true,
            },
            id: data.uii,
        });
        await logAdapter.saveLogToSN({ callId: data.uii, paramsType: ParamsType.CALL });
    }

    private async _handleNewCall(data: OnNewCall) {
        const serviceNowAdapter = this.mServiceNowAdapter!;
        serviceNowAdapter.getLogAdapter().initStorageLog({
            params: data,
            paramsType: ParamsType.CALL,
            id: data.uii,
        });
        serviceNowAdapter.showOpenFrame();
    }

    private _handleAcdCallbackReady() {
        this.mServiceNowAdapter!.showOpenFrame();
    }

    private _handleGetCallLink(data: OnGetCallLink) {
        const serviceNowAdapter = this.mServiceNowAdapter!;
        return serviceNowAdapter
            .getLogAdapter()
            .getCRMLink<CallLink>(data.calls?.map((call) => call.uii!) || [], CallLogStorage, 'crmCallLog');
    }
    private async _handlePendingDisposition(data: OnDispCall) {
        const logAdapter = this.mServiceNowAdapter!.getLogAdapter();
        const rcxAdapter = this.mRCXAdapter!;
        const localData = await logAdapter.getStorageLog(data.uii, ParamsType.CALL);
        if (!localData) {
            await logAdapter.initStorageLog({
                params: data,
                paramsType: ParamsType.CALL,
                id: data.uii,
            });
        }
        await logAdapter.updateStorageLogAndContact({
            rawData: {
                notes: data.notes,
                disposition: data.disposition,
                recordingUrl: data.recordingUrl,
                duration: data.duration,
                summary: data.summary,
                transcriptUrl: data.transcriptUrl,
                leadName:
                    data?.lead?.firstName && data?.lead?.lastName
                        ? `${data?.lead.firstName} ${data?.lead.lastName}`
                        : data?.lead?.firstName || data?.lead?.lastName || '',
            },
            id: data.uii,
            paramsType: ParamsType.CALL,
        });

        await logAdapter.saveLogToSN({
            callId: data.uii,
            paramsType: ParamsType.CALL,
        });
        rcxAdapter.sendDispositionResult({
            success: true,
            data: { uii: data.uii },
        });
    }
    private async _handleSubmitLog(payload: OnSubmitLog) {
        const logAdapter = this.mServiceNowAdapter!.getLogAdapter();
        if (payload && payload.type === ParamsType.CALL) {
            if (payload.serviceNowLogInfo) {
                // on call case update log info
                if (payload.uii) {
                    const localData = await logAdapter.getStorageLog(payload.uii, ParamsType.CALL);
                    if (!localData) {
                        await logAdapter.initStorageLog({
                            params: {} as CallBase,
                            paramsType: ParamsType.CALL,
                            id: payload.uii,
                        });
                    }
                    await logAdapter.updateStorageLogAndContact({
                        id: payload.uii!,
                        rawData: mapLogInfo(payload.serviceNowLogInfo),
                        paramsType: ParamsType.CALL,
                    });
                }

                const data = payload.params as SubmitCall;
                // On call history submit log
                if (data && data.uii) {
                    await logAdapter.initStorageLog({
                        params: data,
                        paramsType: ParamsType.CALL,
                        id: data.uii,
                    });
                    await logAdapter.updateStorageLogAndContact({
                        id: data.uii,
                        rawData: {
                            notes: data.notes,
                            disposition: data.disposition,
                            recordingUrl: data.recordingUrl,
                            transcriptUrl: data.transcriptUrl,
                            duration: data.duration,
                            summary: data.summary,
                            ...mapLogInfo(payload.serviceNowLogInfo),
                            isEnded: data.source === SourceType.CALL_PAGE ? false : true,
                        },
                        paramsType: ParamsType.CALL,
                    });
                    await logAdapter.saveLogToSN({
                        callId: data.uii,
                        paramsType: ParamsType.CALL,
                    });
                }

                return {
                    success: true,
                };
            }
            return {
                success: false,
                error: { message: 'Wrong params' },
            };
        }
    }
}

export default new CallLogModule();
